// Preferências do usuário (P0.8) — tabela profiles.
import { POMODORO_DEFAULTS, validatePomodoroConfig, type BreakMode } from './pomodoro';

/** Idiomas do app (decisão do usuário em 21/10/2026: + espanhol, francês, alemão, pt-PT e italiano). */
export const LANGUAGES = ['pt-BR', 'en', 'es', 'fr', 'de', 'it', 'pt-PT'] as const;
export type Language = (typeof LANGUAGES)[number];
export type ThemePref = 'light' | 'dark' | 'system';
export type TimeFormat = '12h' | '24h';

export interface Profile {
  id: string;
  name: string;
  /** null = ainda não escolheu: segue o idioma do sistema */
  language: Language | null;
  theme: ThemePref;
  pomodoro_enabled: boolean;
  pomodoro_focus_min: number;
  /** descanso em segundos: de 1 s a 24 h (ASM-033) */
  pomodoro_break_sec: number;
  /** quando descansar: a cada bloco de foco ou ao finalizar cada tarefa (AC-068) */
  pomodoro_break_mode: BreakMode;
  float_auto_open: boolean;
  sounds_enabled: boolean;
  /** horários em 12h (2:30 PM) ou 24h (14:30) — AC-126 */
  time_format: TimeFormat;
  /** já respondeu as perguntas da primeira vez (uma vez por conta) — AC-127 */
  onboarding_done: boolean;
  /** tarefas recorrentes ligadas (AC-216); contas novas começam ligadas (ASM-121) */
  recurring_enabled: boolean;
  /** meme e confetes ao concluir; desligada, só um "Feito!" verde de 0,5 s (AC-230) */
  celebration_enabled: boolean;
}

export type ProfilePatch = Partial<Omit<Profile, 'id'>>;

/** Padrões da spec: Pomodoro desligado (25 min de foco / 5 min de pausa), janela flutuante abre sozinha, sons ligados. */
export const DEFAULT_PREFS: Omit<Profile, 'id' | 'name'> = {
  language: null,
  theme: 'system',
  pomodoro_enabled: false,
  pomodoro_focus_min: POMODORO_DEFAULTS.focusMin,
  pomodoro_break_sec: POMODORO_DEFAULTS.breakSec,
  pomodoro_break_mode: 'block',
  float_auto_open: true,
  sounds_enabled: true,
  time_format: '24h',
  onboarding_done: false,
  recurring_enabled: true,
  celebration_enabled: true,
};

/** Formato comum de cada idioma, marcado no pop-up inicial (ASM-062). */
export function defaultTimeFormat(language: Language): TimeFormat {
  return language === 'en' ? '12h' : '24h';
}

/**
 * Primeira abertura: segue o idioma do sistema. Português do Brasil (ou "pt" sem país) →
 * pt-BR; português de outros países → pt-PT; es / fr / de / it; o resto → inglês.
 */
export function detectLanguage(systemLanguages: readonly string[]): Language {
  const [lang = '', region = ''] = (systemLanguages[0] ?? '').replace('_', '-').toLowerCase().split('-');
  if (lang === 'pt') return !region || region === 'br' ? 'pt-BR' : 'pt-PT';
  if (lang === 'es' || lang === 'fr' || lang === 'de' || lang === 'it') return lang;
  return 'en';
}

export function isLanguage(v: unknown): v is Language {
  return typeof v === 'string' && (LANGUAGES as readonly string[]).includes(v);
}

export function resolveLanguage(profile: Pick<Profile, 'language'> | null, systemLanguages: readonly string[]): Language {
  return profile?.language ?? detectLanguage(systemLanguages);
}

export function resolveTheme(pref: ThemePref, systemPrefersDark: boolean): 'light' | 'dark' {
  if (pref === 'system') return systemPrefersDark ? 'dark' : 'light';
  return pref;
}

export type ProfileError =
  | 'name_too_long'
  | 'focus_out_of_range'
  | 'break_out_of_range'
  | 'invalid_language'
  | 'invalid_theme'
  | 'invalid_break_mode'
  | 'invalid_time_format';

export function validateProfilePatch(patch: ProfilePatch): ProfileError[] {
  const errors: ProfileError[] = [];
  if (patch.name !== undefined && [...patch.name].length > 80) errors.push('name_too_long');
  if (patch.language !== undefined && patch.language !== null && !isLanguage(patch.language)) {
    errors.push('invalid_language');
  }
  if (patch.theme !== undefined && !['light', 'dark', 'system'].includes(patch.theme)) errors.push('invalid_theme');
  if (patch.time_format !== undefined && !['12h', '24h'].includes(patch.time_format)) errors.push('invalid_time_format');
  if (patch.pomodoro_break_mode !== undefined && !['block', 'task'].includes(patch.pomodoro_break_mode)) errors.push('invalid_break_mode');
  const p = validatePomodoroConfig(
    patch.pomodoro_focus_min ?? POMODORO_DEFAULTS.focusMin,
    patch.pomodoro_break_sec ?? POMODORO_DEFAULTS.breakSec
  );
  if (p.focus) errors.push('focus_out_of_range');
  if (p.break) errors.push('break_out_of_range');
  return errors;
}

/** Palavra para confirmar a exclusão da conta, no idioma do app (ASM-061). */
const DELETE_WORD: Record<Language, string> = {
  'pt-BR': 'EXCLUIR',
  'pt-PT': 'ELIMINAR',
  en: 'DELETE',
  es: 'ELIMINAR',
  fr: 'SUPPRIMER',
  de: 'LÖSCHEN',
  it: 'ELIMINA',
};

export function deleteConfirmWord(language: Language): string {
  return DELETE_WORD[language];
}
